<script lang="ts">
  import { onMount } from 'svelte';
  import { competitorServicesApi } from '@shared/api/competitor-services';
  import { listProducts } from '@shared/api/products';
  import { parsePaged } from '@shared/api/paging';
  import { parseProduct } from '@domain/product/parseProduct';
  import { navigate } from '@shared/router/navigation';
  import { sessionStore } from '@features/auth/session/model/store.svelte';
  import { canEditMasters } from '@shared/types/session';
  import ThirdPartySolutionModal from '@features/third-party-solution-edit/ThirdPartySolutionModal.svelte';
  import {
    EXTERNAL_SOLUTION_KIND_OPTIONS,
    activeCompetitorSettingLabel,
    externalSolutionKind,
    filterThirdPartySolutions,
    mergeThirdPartySolutionSummary,
    normalizeThirdPartySolutionErrorMessage,
    type ExternalSolutionKindFilter,
  } from '@domain/competitor-service/third-party-solution-edit';
  import type {
    CompetitorService,
    ExternalSolutionKind,
  } from '@shared/types/competitor-service';

  let items = $state<CompetitorService[]>([]);
  let loading = $state(false);
  let errorMessage = $state<string | null>(null);
  let q = $state('');
  let kindFilter = $state<ExternalSolutionKindFilter>('all');
  let createOpen = $state(false);
  let blockedProductNames = $state<string[]>([]);
  let savingKindIds = $state<Set<string>>(new Set());

  let allowEdit = $derived(
    sessionStore.user ? canEditMasters(sessionStore.user.role) : false,
  );

  const filteredItems = $derived(
    filterThirdPartySolutions(items, q, kindFilter),
  );

  async function load(): Promise<void> {
    loading = true;
    errorMessage = null;
    try {
      const res = await competitorServicesApi.list();
      items = res.items;
    } catch (err) {
      errorMessage =
        err instanceof Error
          ? err.message
          : '外部ソリューションを読み込めませんでした';
    } finally {
      loading = false;
    }
  }

  async function loadBlockedProductNames(): Promise<void> {
    const names = new Set<string>();
    const pageSize = 200;
    try {
      for (let page = 1; ; page += 1) {
        const res = parsePaged(
          await listProducts({
            sort_by: 'name',
            sort_dir: 'asc',
            page,
            page_size: pageSize,
          }),
          parseProduct,
        );
        for (const item of res.items) {
          names.add(item.name);
        }
        if (res.items.length === 0 || res.page * res.page_size >= res.total)
          break;
      }
    } catch {
      // Backend validation is authoritative; frontend deny-list is only immediate feedback.
    }
    blockedProductNames = Array.from(names);
  }

  function usageCount(item: CompetitorService): number {
    return item.usage_target_count ?? 0;
  }

  function setKindSaving(id: string, saving: boolean): void {
    const next = new Set(savingKindIds);
    if (saving) next.add(id);
    else next.delete(id);
    savingKindIds = next;
  }

  async function saveKind(
    item: CompetitorService,
    nextKind: ExternalSolutionKind,
  ): Promise<void> {
    if (!allowEdit || savingKindIds.has(item.id) || item.kind === nextKind)
      return;
    setKindSaving(item.id, true);
    errorMessage = null;
    try {
      const saved = await competitorServicesApi.patch(item.id, {
        kind: nextKind,
      });
      items = mergeThirdPartySolutionSummary(items, saved);
    } catch (err) {
      errorMessage =
        err instanceof Error
          ? normalizeThirdPartySolutionErrorMessage(err.message)
          : '区分の保存に失敗しました';
    } finally {
      setKindSaving(item.id, false);
    }
  }

  function gotoDetail(ev: MouseEvent, id: string): void {
    ev.preventDefault();
    navigate(`/third-party-solutions/${id}`);
  }

  function handleSaved(solution: CompetitorService): void {
    createOpen = false;
    navigate(`/third-party-solutions/${solution.id}`);
  }

  onMount(() => {
    void load();
    void loadBlockedProductNames();
  });
</script>

<header class="page-head">
  <div>
    <h1>外部ソリューション</h1>
  </div>
  <button
    type="button"
    class="btn btn-primary"
    disabled={!allowEdit}
    title={allowEdit ? '' : '権限がありません'}
    onclick={() => (createOpen = true)}
  >
    + 外部ソリューションを追加
  </button>
</header>

<div class="panel tools">
  <div
    class="segments"
    role="group"
    aria-label="外部ソリューション区分フィルタ"
  >
    <button
      type="button"
      class:active={kindFilter === 'all'}
      onclick={() => (kindFilter = 'all')}
    >
      すべて
    </button>
    <button
      type="button"
      class:active={kindFilter === 'competitor'}
      onclick={() => (kindFilter = 'competitor')}
    >
      競合
    </button>
    <button
      type="button"
      class:active={kindFilter === 'referral'}
      onclick={() => (kindFilter = 'referral')}
    >
      紹介先
    </button>
    <button
      type="button"
      class:active={kindFilter === 'other'}
      onclick={() => (kindFilter = 'other')}
    >
      その他
    </button>
  </div>
  <input
    type="search"
    class="search-input"
    bind:value={q}
    placeholder="外部ソリューション名"
    autocomplete="off"
    data-testid="third-party-solution-search"
  />
</div>

<section class="panel">
  {#if loading && items.length === 0}
    <p class="muted">読み込み中…</p>
  {:else if errorMessage}
    <p class="error" role="alert">{errorMessage}</p>
  {:else if filteredItems.length === 0}
    <p class="muted">該当する外部ソリューションがありません。</p>
  {:else}
    <div class="table-scroll">
      <table data-testid="third-party-solutions-table">
        <colgroup>
          <col class="col-third-party-name-width" />
          <col class="col-kind-width" />
          <col class="col-competitor-setting-width" />
          <col class="col-usage-width" />
        </colgroup>
        <thead>
          <tr>
            <th>外部ソリューション名</th>
            <th>区分</th>
            <th>競合ソリューション</th>
            <th>利用先</th>
          </tr>
        </thead>
        <tbody>
          {#each filteredItems as item (item.id)}
            {@const kind = externalSolutionKind(item)}
            <tr>
              <td>
                <a
                  href={`/third-party-solutions/${item.id}`}
                  class="primary-link"
                  onclick={(ev) => gotoDetail(ev, item.id)}
                >
                  <strong>{item.name}</strong>
                </a>
              </td>
              <td>
                <select
                  class="kind-select"
                  class:competitor={kind === 'competitor'}
                  class:referral={kind === 'referral'}
                  value={kind}
                  disabled={!allowEdit || savingKindIds.has(item.id)}
                  aria-label={`${item.name} の区分`}
                  data-testid="external-solution-kind-select"
                  onchange={(ev) =>
                    void saveKind(
                      item,
                      (ev.currentTarget as HTMLSelectElement)
                        .value as ExternalSolutionKind,
                    )}
                >
                  {#each EXTERNAL_SOLUTION_KIND_OPTIONS as option}
                    <option value={option.value}>{option.label}</option>
                  {/each}
                </select>
              </td>
              <td>{activeCompetitorSettingLabel(item)}</td>
              <td class="numeric">
                <a
                  href={`/third-party-solutions/${item.id}`}
                  onclick={(ev) => gotoDetail(ev, item.id)}
                >
                  {usageCount(item)}
                </a>
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  {/if}
</section>

<ThirdPartySolutionModal
  open={createOpen}
  blockedNames={blockedProductNames}
  blockedNameMessage="自社ソリューション名と同名の外部ソリューションは登録できません"
  onClose={() => (createOpen = false)}
  onSaved={handleSaved}
/>

<style>
  .page-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 0 0 12px;
    flex-wrap: wrap;
  }
  .page-head h1 {
    margin: 0;
    font-size: 1.25rem;
  }
  .page-head .btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .tools {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    align-items: center;
  }
  /* 検索窓は操作エリアの右側へ。折り返し時は単独行で全幅になる。 */
  .tools .search-input {
    margin-left: auto;
    min-width: min(320px, 100%);
  }
  .segments {
    display: inline-flex;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    overflow: hidden;
    background: #fff;
  }
  .segments button {
    border: 0;
    border-right: 1px solid #d1d5db;
    background: #fff;
    color: #374151;
    padding: 7px 10px;
    font: inherit;
    font-weight: 700;
    cursor: pointer;
  }
  .segments button:last-child {
    border-right: 0;
  }
  .segments button.active {
    background: #1f2937;
    color: #fff;
  }
  .table-scroll {
    max-height: max(360px, calc(100vh - 280px));
    overflow: auto;
    border-top: 1px solid var(--border, #e3e6eb);
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
  }
  table {
    width: 100%;
    table-layout: fixed;
  }
  .col-third-party-name-width {
    width: 32%;
  }
  .col-kind-width {
    width: 112px;
  }
  .col-competitor-setting-width {
    width: auto;
  }
  .col-usage-width {
    width: 96px;
  }
  th,
  td {
    vertical-align: middle;
    overflow-wrap: anywhere;
  }
  thead th {
    position: sticky;
    top: 0;
    z-index: 3;
    background: #f9fafb;
    box-shadow: 0 1px 0 var(--border, #e3e6eb);
  }
  .primary-link {
    color: var(--primary, #2563eb);
    text-decoration: none;
  }
  .primary-link:hover {
    text-decoration: underline;
  }
  .kind-select {
    width: 88px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    padding: 4px 7px;
    background: #f9fafb;
    color: #374151;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.4;
  }
  .kind-select.competitor {
    border-color: #bfdbfe;
    background: #eff6ff;
    color: #1d4ed8;
  }
  .kind-select.referral {
    border-color: #bbf7d0;
    background: #f0fdf4;
    color: #15803d;
  }
  .kind-select:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
  .numeric {
    text-align: center;
    font-variant-numeric: tabular-nums;
  }
  .muted {
    color: var(--muted, #6b7280);
  }
  .error {
    color: #b91c1c;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 6px;
    padding: 10px 12px;
  }
</style>
